import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import CrmLossReasonModal from "./CrmLossReasonModal";
import { buildLossExtra1 } from "../../utils/crmLossReason";

const STAGE_STYLES = {
  Pending: { bg: "rgba(59, 130, 246, 0.15)", border: "rgba(59, 130, 246, 0.45)", color: "#93c5fd" },
  "In Progress": { bg: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.45)", color: "#fcd34d" },
  Converted: { bg: "rgba(34, 197, 94, 0.15)", border: "rgba(34, 197, 94, 0.45)", color: "#86efac" },
  "Not Interested": { bg: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.45)", color: "#fca5a5" },
};

const MANUAL_STAGES = [
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Not Interested", label: "Not interested", needsLossReason: true },
  { value: "Converted", label: "Converted", adminOnly: true },
];

const MENU_WIDTH = 168;

function clampMenuLeft(left) {
  const maxLeft = window.innerWidth - MENU_WIDTH - 8;
  return Math.max(8, Math.min(left, maxLeft));
}

const CrmLeadStageControl = ({
  lead,
  leadName,
  isStaffUser = false,
  onStatusChange,
}) => {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [lossModalOpen, setLossModalOpen] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);
  const [saving, setSaving] = useState(false);

  const stage = lead?.extra || "";
  const style = STAGE_STYLES[stage] || {
    bg: "rgba(148, 163, 184, 0.12)",
    border: "rgba(148, 163, 184, 0.35)",
    color: "var(--text-secondary, #94a3b8)",
  };

  const menuItems = MANUAL_STAGES.filter(
    (item) => !item.adminOnly || !isStaffUser
  ).filter((item) => item.value !== stage);

  const updateMenuPosition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: clampMenuLeft(rect.right - MENU_WIDTH),
    });
  };

  useLayoutEffect(() => {
    if (!menuOpen) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setMenuOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const applyStage = async (newStatus, lossExtra1 = null) => {
    setSaving(true);
    try {
      await onStatusChange(newStatus, lead, lossExtra1);
      setMenuOpen(false);
      setLossModalOpen(false);
      setPendingStage(null);
    } finally {
      setSaving(false);
    }
  };

  const handlePick = (item) => {
    if (item.needsLossReason) {
      setPendingStage(item.value);
      setLossModalOpen(true);
      setMenuOpen(false);
      return;
    }
    applyStage(item.value);
  };

  const confirmLoss = async (reasonId, note) => {
    await applyStage(pendingStage || "Not Interested", buildLossExtra1(reasonId, note));
  };

  const toggleMenu = (event) => {
    event.stopPropagation();
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    updateMenuPosition();
    setMenuOpen(true);
  };

  const menuPortal =
    menuOpen &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={menuRef}
        className="crm-lead-stage__menu crm-lead-stage__menu--portal"
        role="menu"
        style={{ top: menuPos.top, left: menuPos.left }}
      >
        <p className="crm-lead-stage__menu-hint">Manual override only</p>
        {menuItems.map((item) => (
          <button
            key={item.value}
            type="button"
            role="menuitem"
            className="crm-lead-stage__menu-item"
            onClick={() => handlePick(item)}
            disabled={saving}
          >
            {item.label}
          </button>
        ))}
      </div>,
      document.body
    );

  return (
    <>
      <div className="crm-lead-stage lsuv-card__action">
        <span
          className="crm-lead-stage__badge"
          style={{
            background: style.bg,
            borderColor: style.border,
            color: style.color,
          }}
          title="Pipeline stage — use quick actions above to log call outcomes"
        >
          {stage || "No stage"}
        </span>

        {menuItems.length > 0 && (
          <div className="crm-lead-stage__adjust">
            <button
              ref={btnRef}
              type="button"
              className={`crm-lead-stage__adjust-btn${menuOpen ? " is-open" : ""}`}
              onClick={toggleMenu}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              disabled={saving}
            >
              Adjust
              <ChevronDown size={14} />
            </button>
          </div>
        )}
      </div>

      {menuPortal}

      <CrmLossReasonModal
        open={lossModalOpen}
        leadName={leadName}
        onConfirm={confirmLoss}
        onCancel={() => !saving && setLossModalOpen(false)}
        saving={saving}
      />
    </>
  );
};

export default CrmLeadStageControl;
